import { create } from 'zustand';
import { Timestamp } from 'firebase/firestore';
import { headerHeight, bannerHeight } from '@/constants/header';
import { AppMessage, MessageRenderContext } from '@/types/message';
import { getActiveMessage } from '@/services/messageService';

interface BannerState {
  headerHeight: number;
  message: AppMessage | null;
  fetchMessage: (ctx: MessageRenderContext) => Promise<void>;
  showUpgradeBanner: () => void;
  showTrialBanner: (daysRemaining: number) => void;
  showGracePeriodBanner: (daysRemaining: number, hoursRemaining: number, minutesRemaining: number) => void;
  closeBanner: () => void;
  updateHeaderHeight: () => void;
  isDismissed: (messageId: string) => boolean;
}

export const useBannerStore = create<BannerState>((set, get) => ({
  headerHeight: headerHeight,
  message: null,

  fetchMessage: async (ctx: MessageRenderContext) => {
    const message = await getActiveMessage(ctx);

    // Check if message is dismissed
    if (message && get().isDismissed(message.id)) {
      set({ message: null });
    } else {
      set({ message });
    }

    get().updateHeaderHeight();
  },

  showUpgradeBanner: () => {
    set({
      message: {
        priority: 2, // Lower priority than grace period and trial banners
        startDate: Timestamp.now(),
        endDate: Timestamp.now(),
        active: true,
        id: 'upgrade-message',
        message: 'Note creation limit reached. Upgrade to continue.',
        severity: 'info',
        url: '/plan-and-payments',
      },
    });
    get().updateHeaderHeight();
  },

  showTrialBanner: (daysRemaining: number) => {
    set({
      message: {
        priority: 1, // Lower priority than grace period banner
        startDate: Timestamp.now(),
        endDate: Timestamp.now(),
        active: true,
        id: 'trial-banner',
        message: `Pro trial expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`,
        severity: 'info',
        url: '/plan-and-payments',
      },
    });
    get().updateHeaderHeight();
  },

  showGracePeriodBanner: (daysRemaining: number, hoursRemaining: number, minutesRemaining: number) => {
    // Format the time remaining message
    let timeMessage = '';
    if (daysRemaining > 0) {
      timeMessage = `${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`;
    } else if (hoursRemaining > 0) {
      timeMessage = `${hoursRemaining} hour${hoursRemaining === 1 ? '' : 's'}`;
    } else if (minutesRemaining > 0) {
      timeMessage = `${minutesRemaining} minute${minutesRemaining === 1 ? '' : 's'}`;
    } else {
      // Edge case: less than 1 minute remaining
      timeMessage = 'less than 1 minute';
    }

    set({
      message: {
        priority: 0, // Highest priority - shows over trial and upgrade banners
        startDate: Timestamp.now(),
        endDate: Timestamp.now(),
        active: true,
        id: 'grace-period-banner',
        message: `Payment failed. Update payment details within ${timeMessage} to avoid service suspension`,
        severity: 'error',
        url: '/plan-and-payments',
      },
    });
    get().updateHeaderHeight();
  },

  updateHeaderHeight: () => {
    const { message } = get();
    const banner = message ? bannerHeight : 0;
    set({ headerHeight: headerHeight + banner });
  },

  closeBanner: () => {
    const { message } = get();
    if (message) {
      // Store dismissal with 24-hour expiration
      const dismissalKey = `banner_dismissed_${message.id}`;
      const dismissalData = {
        messageId: message.id,
        dismissedAt: Date.now(),
        expiresAt: Date.now() + 24 * 60 * 60 * 1000, // 24 hours
      };
      localStorage.setItem(dismissalKey, JSON.stringify(dismissalData));
    }

    set({ message: null });
    get().updateHeaderHeight();
  },

  isDismissed: (messageId: string) => {
    const dismissalKey = `banner_dismissed_${messageId}`;
    const dismissalData = localStorage.getItem(dismissalKey);

    if (!dismissalData) {
      return false;
    }

    try {
      const parsed = JSON.parse(dismissalData);
      const now = Date.now();

      // Check if dismissal has expired
      if (now > parsed.expiresAt) {
        localStorage.removeItem(dismissalKey);
        return false;
      }

      return true;
    } catch (error) {
      // Invalid data, remove it
      localStorage.removeItem(dismissalKey);
      return false;
    }
  },
}));
